// src/Store/reportsSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
// Assuming the following imports are correctly set up in your environment
import Unauthorized from "../Admin/Unauthorized"; 
import BaseUrl from '../../BaseUrl';

// --- API Endpoints ---
const PATIENT_API_URL = `${BaseUrl.authurl}Auth/GetPatients`;
const CONSULTATIONS_HISTORY_API_URL = `${BaseUrl.consultationurl}Consultations/GetPatientConsultations`;
const CONSULTATION_DETAIL_API_URL = `${BaseUrl.consultationurl}Consultations/GetConsultationById`; 

// 🔑 CORRECT API Endpoints for Master Data
const LAB_SERVICE_MASTER_API_URL = `${BaseUrl.labsurl}LabTestServices`;
const PACKAGE_MASTER_API_URL = `${BaseUrl.labsurl}Packages`;
const PACKAGE_SERVICES_API_URL = `${BaseUrl.labsurl}PackageServices`;

// Get Authorization Headers
const getHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'accept': 'text/plain',
    'Content-Type': 'application/json'
  };
};

// --- Async Thunks ---

// Existing Async Thunk for Patient Search
export const fetchPatientSearchData = createAsyncThunk(
  'reports/fetchPatientSearchData', 
  async (searchQuery, { rejectWithValue }) => {
    if (searchQuery.length <= 2) {
      return []; 
    }
    
    const url = `${PATIENT_API_URL}?id=${searchQuery}`; 

    try {
      const headers = getHeaders();
      const response = await axios.get(url, { headers });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch search data.');
    }
  }
);

// Updated Async Thunk for Patient Consultation History with dynamic status
export const fetchPatientConsultations = createAsyncThunk(
  'reports/fetchPatientConsultations',
  async ({ mrn, status, consultationType }, { rejectWithValue }) => { 
    // Build URL with status parameter if provided
    let url = `${CONSULTATIONS_HISTORY_API_URL}?MRN=${mrn}`;
    if (status && status !== '') {
      url += `&status=${status}`;
    }
    if (consultationType) {
      url += `&ConsultationType=${consultationType}`;
    }
    
    try {
      const headers = getHeaders();
      const response = await axios.get(url, { headers }); 
      return response.data; 
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch consultation history.');
    }
  }
);

// Fetch consultation details with enriched lab test and package data
export const fetchConsultationDetails = createAsyncThunk(
  'reports/fetchConsultationDetails',
  async ({ consultationId, consultationType }, { getState, rejectWithValue }) => {
      const url = `${CONSULTATION_DETAIL_API_URL}?ConsultationId=${consultationId}&Type=${consultationType}`; 

      try {
          const headers = getHeaders();
          const response = await axios.get(url, { headers }); 
          const consultationData = response.data;
          
          // Get master data from state
          const { labServiceMaster, packageMaster, packageServicesMapping } = getState().reports;
          
          // Enrich PatientServiceList with service names from master data
          if (consultationData.PatientServiceList && consultationData.PatientServiceList.length > 0) {
            consultationData.PatientServiceList = consultationData.PatientServiceList.map(service => {
              const masterService = labServiceMaster[service.ServiceId];
              return {
                ...service,
                Id: service.Id || service.id,
                ServiceId: service.ServiceId,
                ServiceName: masterService?.serviceName || masterService?.name || `Service ID: ${service.ServiceId}`,
                Description: masterService?.description || '',
                Category: masterService?.category || '',
                UnitOfMeasure: masterService?.unitOfMeasure || '',
                NormalRange: masterService?.minVal && masterService?.maxVal 
                  ? `${masterService.minVal} - ${masterService.maxVal} ${masterService.unitOfMeasure || ''}`
                  : 'N/A',
                Result: service.Result || 0,
                Status: service.Status || service.ServiceStatus || 'pending',
                CompletionDate: service.CompletionDate
              };
            });
          }
          
          // Enrich PatientPackageList with package names and their services
          if (consultationData.PatientPackageList && consultationData.PatientPackageList.length > 0) {
            consultationData.PatientPackageList = consultationData.PatientPackageList.map(pkg => {
              const masterPackage = packageMaster[pkg.PackageId];
              const packageServiceIds = packageServicesMapping[pkg.PackageId] || [];
              
              // Get all services for this package
              const packageServices = packageServiceIds.map(serviceId => {
                const service = labServiceMaster[serviceId];
                return service ? {
                  id: service.id,
                  serviceName: service.serviceName,
                  description: service.description,
                  category: service.category,
                  unitOfMeasure: service.unitOfMeasure,
                  normalRange: service.minVal && service.maxVal 
                    ? `${service.minVal} - ${service.maxVal} ${service.unitOfMeasure || ''}`
                    : 'N/A'
                } : null;
              }).filter(s => s !== null);
              
              return {
                ...pkg,
                Id: pkg.Id || pkg.id,
                PackageId: pkg.PackageId,
                PackageName: masterPackage?.name || masterPackage?.packageName || `Package ID: ${pkg.PackageId}`,
                PackageDescription: masterPackage?.description || '',
                PackagePrice: masterPackage?.price || 0,
                Services: packageServices,
                Status: pkg.Status || pkg.PackageStatus || 'pending',
                CompletionDate: pkg.CompletionDate
              };
            });
          }
          
          return consultationData;
      } catch (error) {
          return rejectWithValue(error.response?.data || 'Failed to fetch consultation details.');
      }
  }
);

// 🔑 NEW: Async Thunk for Lab Service Master Data
export const fetchLabServiceMasterData = createAsyncThunk(
    'reports/fetchLabServiceMasterData',
    async (_, { rejectWithValue }) => {
        try {
            const headers = getHeaders();
            const response = await axios.get(LAB_SERVICE_MASTER_API_URL, { 
              headers,
              params: { _t: Date.now() }
            }); 
            // Normalize data into an object map for easy lookup: { id: serviceObject, ... }
            const normalizedData = response.data.reduce((acc, service) => {
                acc[service.id] = service;
                return acc;
            }, {});
            return normalizedData;
        } catch (error) {
            console.error('Error fetching lab services:', error);
            return rejectWithValue(error.response?.data || 'Failed to fetch lab service master data.');
        }
    }
);

// 🔑 NEW: Async Thunk for Package Master Data
export const fetchPackageMasterData = createAsyncThunk(
    'reports/fetchPackageMasterData',
    async (_, { rejectWithValue }) => {
        try {
            const headers = getHeaders();
            const response = await axios.get(PACKAGE_MASTER_API_URL, { 
              headers,
              params: { _t: Date.now() }
            }); 
            // Normalize data into an object map for easy lookup: { id: packageObject, ... }
            const normalizedData = response.data.reduce((acc, pkg) => {
                acc[pkg.id] = pkg;
                return acc;
            }, {});
            return normalizedData;
        } catch (error) {
            console.error('Error fetching packages:', error);
            return rejectWithValue(error.response?.data || 'Failed to fetch package master data.');
        }
    }
);

// 🔑 NEW: Async Thunk for Package Services Mapping
export const fetchPackageServicesMapping = createAsyncThunk(
    'reports/fetchPackageServicesMapping',
    async (_, { rejectWithValue }) => {
        try {
            const headers = getHeaders();
            const response = await axios.get(PACKAGE_SERVICES_API_URL, { 
              headers,
              params: { _t: Date.now() }
            });
            // Create mapping: { packageId: [serviceId1, serviceId2, ...] }
            const mapping = response.data.reduce((acc, item) => {
                if (!acc[item.packageId]) {
                    acc[item.packageId] = [];
                }
                acc[item.packageId].push(item.serviceId);
                return acc;
            }, {});
            return mapping;
        } catch (error) {
            console.error('Error fetching package services mapping:', error);
            return {};
        }
    }
);

// --- Reports Slice Definition ---
export const reportsSlice = createSlice({
  name: 'reports',
  initialState: {
    searchTerm: '',
    patientData: [], 
    loading: 'idle', // for patient search
    error: null,
    
    // State for Consultations History
    consultations: [],
    consultationLoading: 'idle', 
    consultationError: null,
    selectedPatient: null,
    consultationStatus: 'Completed', // Default status set to 'Completed'
    consultationType: 'out_patient', // Default consultation type

    // State for Detailed Consultation Report
    detailedConsultation: null,
    detailLoading: 'idle',
    detailError: null,
    
    // 🔑 NEW: State for Master Data
    labServiceMaster: {}, // Map of { id: serviceObject }
    packageMaster: {},  // Map of { id: packageObject }
    packageServicesMapping: {}, // Map of { packageId: [serviceIds] }
    masterLoading: 'idle',
    masterError: null,
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearPatientData: (state) => {
      state.patientData = [];
      state.consultations = [];
      state.selectedPatient = null; 
      state.consultationLoading = 'idle'; 
      state.consultationError = null;
      // Reset detail state
      state.detailedConsultation = null; 
      state.detailLoading = 'idle';
      state.detailError = null;
    },
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    // Reducer to clear detailed consultation data
    clearDetailedConsultation: (state) => {
      state.detailedConsultation = null;
      state.detailLoading = 'idle';
      state.detailError = null;
    },
    // 🔑 NEW: Reducer to set consultation status
    setConsultationStatus: (state, action) => {
      state.consultationStatus = action.payload;
    },
    // 🔑 NEW: Reducer to set consultation type
    setConsultationType: (state, action) => {
      state.consultationType = action.payload;
    }
  },
  
  extraReducers: (builder) => {
    builder
      // --- Patient Search Thunk Reducers (Unchanged) ---
      .addCase(fetchPatientSearchData.pending, (state) => {
        state.loading = 'loading';
        state.error = null;
      })
      .addCase(fetchPatientSearchData.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.patientData = action.payload;
      })
      .addCase(fetchPatientSearchData.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.payload;
        state.patientData = []; 
      })
      
      // --- Consultation History Thunk Reducers (Updated) ---
      .addCase(fetchPatientConsultations.pending, (state) => {
        state.consultationLoading = 'loading';
        state.consultationError = null;
        state.consultations = []; 
      })
      .addCase(fetchPatientConsultations.fulfilled, (state, action) => {
        state.consultationLoading = 'succeeded';
        // Handle different response structures based on API
        if (action.payload && typeof action.payload === 'object') {
          if (action.payload.onlineResult1 || action.payload.opResult1 || action.payload.ipResult1) {
            const allConsultations = [];
            if (action.payload.onlineResult1?.Consultations) {
              allConsultations.push(...action.payload.onlineResult1.Consultations);
            }
            if (action.payload.opResult1?.Consultations) {
              allConsultations.push(...action.payload.opResult1.Consultations);
            }
            if (action.payload.ipResult1?.Consultations) {
              allConsultations.push(...action.payload.ipResult1.Consultations);
            }
            state.consultations = allConsultations;
          } else if (action.payload.Consultations) {
            state.consultations = action.payload.Consultations;
          } else if (Array.isArray(action.payload)) {
            state.consultations = action.payload;
          } else {
            state.consultations = [];
          }
        } else {
          state.consultations = [];
        }
      })
      .addCase(fetchPatientConsultations.rejected, (state, action) => {
        state.consultationLoading = 'failed';
        state.consultationError = action.payload;
        state.consultations = [];
      })

      // --- Detailed Consultation Thunk Reducers (Updated with enrichment) ---
      .addCase(fetchConsultationDetails.pending, (state) => {
          state.detailLoading = 'loading';
          state.detailError = null;
          state.detailedConsultation = null;
      })
      .addCase(fetchConsultationDetails.fulfilled, (state, action) => {
          state.detailLoading = 'succeeded';
          state.detailedConsultation = action.payload;
      })
      .addCase(fetchConsultationDetails.rejected, (state, action) => {
          state.detailLoading = 'failed';
          state.detailError = action.payload;
          state.detailedConsultation = null;
      })
    
    // 🔑 NEW: Lab Service Master Thunk Reducers
    .addCase(fetchLabServiceMasterData.pending, (state) => {
        state.masterLoading = 'loading';
        state.masterError = null;
    })
    .addCase(fetchLabServiceMasterData.fulfilled, (state, action) => {
        state.masterLoading = 'succeeded';
        state.labServiceMaster = action.payload;
    })
    .addCase(fetchLabServiceMasterData.rejected, (state, action) => {
        state.masterLoading = 'failed';
        state.masterError = action.payload;
        state.labServiceMaster = {};
    })

    // 🔑 NEW: Package Master Thunk Reducers
    .addCase(fetchPackageMasterData.pending, (state) => {
        state.masterLoading = 'loading';
        state.masterError = null;
    })
    .addCase(fetchPackageMasterData.fulfilled, (state, action) => {
        state.masterLoading = 'succeeded';
        state.packageMaster = action.payload;
    })
    .addCase(fetchPackageMasterData.rejected, (state, action) => {
        state.masterLoading = 'failed';
        state.masterError = action.payload;
        state.packageMaster = {};
    })

    // 🔑 NEW: Package Services Mapping Thunk Reducers
    .addCase(fetchPackageServicesMapping.pending, (state) => {
        state.masterLoading = 'loading';
    })
    .addCase(fetchPackageServicesMapping.fulfilled, (state, action) => {
        state.packageServicesMapping = action.payload;
    })
    .addCase(fetchPackageServicesMapping.rejected, (state, action) => {
        console.error('Failed to fetch package services mapping:', action.payload);
        state.packageServicesMapping = {};
    });
  },
});

// Export all actions
export const { 
  setSearchTerm, 
  clearPatientData, 
  setSelectedPatient, 
  clearDetailedConsultation, 
  setConsultationStatus,
  setConsultationType // ✅ Added this export
} = reportsSlice.actions;

export default reportsSlice.reducer;